import videojs from 'video.js'
import forEach from 'lodash/forEach.js'
import { registerPluginOnce } from '../utils.js'

const Plugin = videojs.getPlugin('plugin')

// Default options for the plugin.
const defaults = {
  persistVolume: true,
  persistCC: true,
  defaultCC: 'off'
}

/**
 * An advanced Video.js plugin. For more information on the API
 *
 * See: https://blog.videojs.com/feature-spotlight-advanced-plugins/
 */
class NFBPersistOptions extends Plugin {
  /**
   * Create a NFBPersistOptions plugin instance.
   *
   * @param  {Player} player
   *         A Video.js Player instance.
   *
   * @param  {Object} [options]
   *         An optional options object.
   *
   *         While not a core part of the Video.js plugin architecture, a
   *         second argument of options is a convenient way to accept inputs
   *         from your plugin's caller.
   */
  constructor (player, options) {
    // the parent class will add player under this.player
    super(player)
    this.log.level(videojs.log.level())

    this.options = videojs.obj.merge(defaults, options)
    this.onTextTrackChange = this.onTextTrackChange.bind(this)
    this.onVolumeChange = this.onVolumeChange.bind(this)
    this.ccMenuItemList = null

    const playerState = this.player.readyState()
    if (!playerState || playerState < 2) {
      this.player.on('loadedmetadata', () => {
        this.initVolume()
      })

      this.player.on('nfbcaptionready', () => {
        this.initCC()
      })
  
    } else {
      this.initVolume()
      this.initCC()
    }
  }

  initVolume () {
    this.log('Initializing NFB Persist Options plugin')
    try {
      if (this.options.persistVolume) {
        this.restoreVolume()
        this.handleVolumeListeners()
      }
    } catch (e) {
      this.log(e.message)
    }
  }

  initCC () {
    try {
      if (this.player.textTracks().length) {
        this.ccMenuItemList = this.player.controlBar.subsCapsButton.menu
        if (this.options.persistCC) {
          this.restoreCC()
          this.handleCCListeners()
        } else {
          this.selectCC(this.options.defaultCC)
        }
      }
    } catch (e) {
      this.log(e.message)
    }
  }

  restoreVolume () {
    const storedVolume = localStorage.getItem('nfbWebPlayer.volume')
    if (storedVolume) {
      this.player.volume(parseFloat(storedVolume))
    }

    const storedMuted = localStorage.getItem('nfbWebPlayer.muted') === 'true'
    if (storedMuted) {
      this.player.muted(storedMuted)
    }
  }

  handleVolumeListeners () {
    this.player.on('volumechange', this.onVolumeChange)
  }

  onVolumeChange () {
    localStorage.setItem('nfbWebPlayer.volume', this.player.volume())
    localStorage.setItem('nfbWebPlayer.muted', this.player.muted())
  }

  restoreCC () {
    const storedCC = localStorage.getItem('nfbWebPlayer.cc')
    if (storedCC) {
      this.selectCC(storedCC)
    }
  }

  handleCCListeners () {
    forEach(this.ccMenuItemList, item => {
      if (item.constructor.name !== 'CaptionSettingsMenuItem') {
        item.on('click', () => this.onTextTrackChange(item))
        item.on('tap', () => this.onTextTrackChange(item))
      }
    })
  }

  isOffItem (item) {
    return ['subtitles off', 'captions off'].indexOf(item.options().label) !== -1
  }

  onTextTrackChange (menuItem) {
    if (this.isOffItem(menuItem)) {
      localStorage.setItem('nfbWebPlayer.cc', 'off')
    } else {
      localStorage.setItem('nfbWebPlayer.cc', menuItem.track.language)
    }
  }

  selectCC (value) {
    forEach(this.ccMenuItemList, item => {
      if (value === 'off' && this.isOffItem(item)) {
        item.handleClick()
        return false
      } else if (value === item.track.language) {
        item.handleClick()
        return false
      }
    })
  }
}

// Define default values for the plugin's `state` object here.
NFBPersistOptions.defaultState = {}

// Include the version number.
NFBPersistOptions.VERSION = '1.0.0'

// Register the plugin
registerPluginOnce(videojs, 'nfbPersistOptions', NFBPersistOptions)

export default NFBPersistOptions
